import { useMemo, useState } from 'react';
import { QuestionTypeEnum } from '@/models/Question';
import {
  Identifier,
  QuestionViewPropsFor,
} from '@/components/question/view/types';
import {
  BANK_ID,
  DnDToken,
  Placement,
  SlotConfig,
} from '@/utils/questions/dnd/types';

export const cueSlotId = (cueId: Identifier) => `cue-${cueId}`;

export function useCombinationAnswerView(
  props: QuestionViewPropsFor<QuestionTypeEnum.DND_POSITIONAL>,
) {
  const { question, value, mode } = props;
  const onChange = props.mode === 'answering' ? props.onChange : undefined;

  const cues = useMemo(
    () => question.questionColumn.items,
    [question.questionColumn],
  );
  const answers = useMemo(
    () => question.answerColumn.items,
    [question.answerColumn],
  );

  const tokens = useMemo<DnDToken[]>(
    () => answers.map(answer => ({ id: answer.id, content: answer })),
    [answers],
  );

  const slots = useMemo<SlotConfig[]>(
    () => cues.map(cue => ({ id: cueSlotId(cue.id), capacity: 1 })),
    [cues],
  );

  const [bankOrder, setBankOrder] = useState<Identifier[]>([]);

  const placement = useMemo<Placement>(
    () => ({
      ...Object.fromEntries(
        cues.map((cue, index) => {
          const answerId = value?.[index];
          return [cueSlotId(cue.id), answerId == null ? [] : [answerId]];
        }),
      ),
      [BANK_ID]: bankOrder,
    }),
    [cues, value, bankOrder],
  );

  const disabled = mode !== 'answering';

  const handlePlacementChange = (next: Placement) => {
    setBankOrder(next[BANK_ID] ?? []);
    const matches = cues.map(cue => next[cueSlotId(cue.id)]?.at(0) ?? null);
    onChange?.(matches.some(id => id != null) ? matches : null);
  };

  return { cues, tokens, slots, placement, handlePlacementChange, disabled };
}
